// Batches the per-author "has profile ordinals" checks behind the ordinals badge.
// Feed posts each ask for their author; ids requested within one short window go
// out as a single /ordinals/profile_owners request instead of a media stream
// request per author.

export const BADGE_BATCH_WINDOW_MS = 20
export const BADGE_BATCH_MAX_IDS = 100

type Resolver = (hasBadge: boolean) => void

export function createOrdinalsBadgeBatcher(deps: {
    fetchOwners: (userIds: string[]) => Promise<string[] | null>
    fetchOne: (userId: string) => Promise<boolean>
    schedule?: (flush: () => void) => void
}) {
    const schedule = deps.schedule ?? (flush => setTimeout(flush, BADGE_BATCH_WINDOW_MS))
    let pending = new Map<string, Resolver[]>()
    let scheduled = false

    const settle = async (batch: Map<string, Resolver[]>) => {
        const ids = Array.from(batch.keys())
        const owners = await deps.fetchOwners(ids).catch(() => null)
        if (owners) {
            const ownerSet = new Set(owners)
            batch.forEach((resolvers, id) =>
                resolvers.forEach(resolve => resolve(ownerSet.has(id))),
            )
            return
        }
        // Batch endpoint unavailable: fall back to one stream check per author.
        await Promise.all(
            ids.map(async id => {
                const hasBadge = await deps.fetchOne(id).catch(() => false)
                batch.get(id)?.forEach(resolve => resolve(hasBadge))
            }),
        )
    }

    const flush = () => {
        scheduled = false
        const all = pending
        pending = new Map()
        const ids = Array.from(all.keys())
        for (let i = 0; i < ids.length; i += BADGE_BATCH_MAX_IDS) {
            const chunk = new Map(
                ids.slice(i, i + BADGE_BATCH_MAX_IDS).map(id => [id, all.get(id)!]),
            )
            void settle(chunk)
        }
    }

    return (userId: string): Promise<boolean> =>
        new Promise(resolve => {
            const resolvers = pending.get(userId)
            if (resolvers) resolvers.push(resolve)
            else pending.set(userId, [resolve])
            if (!scheduled) {
                scheduled = true
                schedule(flush)
            }
        })
}
